/**
 * OKS-931 (AC10) — Locale-formatted date/time for server-sent ISO-8601.
 *
 * ## Why this is a shared helper and not two call sites
 *
 * `last_synced_at` is rendered on TWO surfaces: the Products page's SyncPanel
 * and the Shopify card on Integrations. PRD D7 says those two "cannot
 * disagree, by construction rather than by discipline" — which is only true
 * if there is one implementation. Two copies of a date format is exactly how
 * two surfaces start reporting the same timestamp differently.
 *
 * ## The defect this fixes
 *
 * `shopify-integration-card.tsx` interpolated the raw ISO string straight
 * into « Dernière synchronisation : %{at} », rendering
 * `2026-08-03T09:15:00Z` to the brand. It was nil-guarded, and OKS-930 could
 * never populate the column, so the defect was unreachable — **this ticket
 * makes it reachable** and therefore has to fix it.
 */

/**
 * @param iso ISO-8601 string from the server.
 * @param language `i18n.language` — anything other than `"en"` formats as FR,
 *   matching the app's FR-default posture.
 * @returns the formatted string, or `null` when `iso` is absent or
 *   unparseable. Callers render nothing rather than "Invalid Date": a broken
 *   timestamp must not become user-visible copy.
 */
export function formatDateTime(
  iso: string | null | undefined,
  language: string,
): string | null {
  if (!iso) return null

  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null

  return parsed.toLocaleDateString(language === "en" ? "en-US" : "fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/**
 * OKS-994 — DATE-ONLY sibling of `formatDateTime`, for server values that are
 * dates rather than instants.
 *
 * ⚠️ Not `formatDateTime` with the time silently ignored. The correction
 * marker's `corrected_at` is `Date#iso8601` ("2026-09-03"), which parses to
 * midnight — so reusing the datetime helper would render « 3 sept. 2026 00:00 »
 * to an operator and invite them to read a real time out of a zero. A value
 * with no clock in it must not be printed with one.
 *
 * Same null posture as its sibling: absent or unparseable renders nothing
 * rather than "Invalid Date".
 */
export function formatDate(
  iso: string | null | undefined,
  language: string,
): string | null {
  if (!iso) return null

  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null

  return parsed.toLocaleDateString(language === "en" ? "en-US" : "fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}
